import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, FormProvider, FieldErrors } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import { PromotionSettingsCard } from './promotion-settings-card'
import { PromotionUsageCard } from './promotion-usage-card'
import { PromotionSchedulingCard } from './promotion-scheduling-card'
import { PromotionCombinationCard } from './promotion-combination-card'
import { PromotionRulesSection } from './promotion-rules-section'
import { PromotionActionsSection } from './promotion-actions-section'
import { PromotionDetails } from '@/lib/api/promotions'
import { useUnsavedChangesBlocker } from '@/hooks/use-unsaved-changes-blocker'
import { useToast } from '@/components/ui/use-toast'

const PAYMENT_METHOD_NAMES = ['paypal', 'card', 'klarna', 'zelle', 'crypto'] as const

type RuleType = 'ContainsProduct' | 'OrderAmountMinimum' | 'OrderMinQuantity' | 'OrderMaxQuantity' | 'MaxProductQuantity' | 'PaymentIntegrationSelected' | 'DoNotContainRecurringProduct' | 'MaxAmount' | 'ExcludeProduct'
type ActionType =
    | 'LineItemAdjustment'
    | 'OrderAdjustment'
    | 'ShippingAdjustment'
    | 'CreateLineItem'
    | 'SubscriptionLineItemFreeTrial'
    | 'BOGO'

// Zod schema for CurrencyOption
const currencyOptionSchema = z.object({
    rate: z.number(),
    amount: z.number(),
    lock: z.boolean(),
    date: z.string(),
})

// Zod schema for CurrencyOptionsType
const currencyOptionsSchema = z.record(currencyOptionSchema).optional()

// True when a currency-options record holds at least one positive amount. Required amount
// fields (minimum/maximum/discount) stay `undefined` in form state until the merchant types a
// value, and CurrencyPriceInput seeds every currency to 0 — so "did the merchant enter a real
// amount?" means "is any amount > 0?".
const hasPositiveAmount = (value?: z.infer<typeof currencyOptionsSchema>) =>
    !!value && Object.values(value).some((option) => typeof option?.amount === 'number' && option.amount > 0)

// Zod schema for Rule
//
// The object fields stay loose (all optional) so the inferred `Rule` type keeps its flat shape
// for the editor components. The per-type "genuinely required" fields are enforced in the
// superRefine below, mirroring the server discriminated union in
// src/app/useCases/promotions/schemas/promotionRule.schema.ts. Messages are i18n keys, resolved
// for display by the rule card (#TAG-1512).
const ruleSchema = z.object({
    type: z.enum(['ContainsProduct', 'OrderAmountMinimum', 'OrderMinQuantity', 'OrderMaxQuantity', 'MaxProductQuantity', 'PaymentIntegrationSelected', 'DoNotContainRecurringProduct', 'MaxAmount', 'ExcludeProduct']),
    productId: z.string().optional(),
    productName: z.string().optional(),
    minimumQuantity: z.number().optional(),
    maximumQuantity: z.number().optional(),
    minimumAmount: currencyOptionsSchema,
    maximumAmount: currencyOptionsSchema,
    orderMinQuantity: z.number().optional(),
    orderMaxQuantity: z.number().optional(),
    excludedProductIds: z.array(z.string()).optional(),
    variantIds: z.array(z.string()).optional(),
    paymentMethodName: z.enum(PAYMENT_METHOD_NAMES).optional(),
}).superRefine((rule, ctx) => {
    const requireField = (isValid: boolean, field: string, messageKey: string) => {
        if (!isValid) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: messageKey })
    }
    switch (rule.type) {
        case 'ContainsProduct':
            requireField(!!rule.productId, 'productId', 'promotion.validation.productRequired')
            break
        case 'OrderAmountMinimum':
            requireField(hasPositiveAmount(rule.minimumAmount), 'minimumAmount', 'promotion.validation.minimumAmountRequired')
            break
        case 'OrderMinQuantity':
            requireField(!!rule.orderMinQuantity && rule.orderMinQuantity >= 1, 'orderMinQuantity', 'promotion.validation.quantityRequired')
            break
        case 'OrderMaxQuantity':
            requireField(!!rule.orderMaxQuantity && rule.orderMaxQuantity >= 1, 'orderMaxQuantity', 'promotion.validation.quantityRequired')
            break
        case 'MaxProductQuantity':
            requireField(!!rule.productId, 'productId', 'promotion.validation.productRequired')
            requireField(!!rule.maximumQuantity && rule.maximumQuantity >= 1, 'maximumQuantity', 'promotion.validation.quantityRequired')
            break
        case 'PaymentIntegrationSelected':
            requireField(!!rule.paymentMethodName, 'paymentMethodName', 'promotion.validation.paymentMethodRequired')
            break
        case 'MaxAmount':
            requireField(hasPositiveAmount(rule.maximumAmount), 'maximumAmount', 'promotion.validation.maximumAmountRequired')
            break
        case 'ExcludeProduct':
            requireField(
                !!rule.excludedProductIds && rule.excludedProductIds.length > 0,
                'excludedProductIds',
                'promotion.validation.excludedProductsRequired',
            )
            break
        // DoNotContainRecurringProduct has no extra required fields.
    }
})

// Zod schema for Action
const actionSchema = z.object({
    type: z.enum([
        'LineItemAdjustment',
        'OrderAdjustment',
        'ShippingAdjustment',
        'CreateLineItem',
        'SubscriptionLineItemFreeTrial',
        'BOGO',
    ]),
    adjustmentType: z.enum(['percentage', 'amount']).optional(),
    adjustmentPercentage: z.number().min(0).max(100).optional(),
    adjustmentAmount: currencyOptionsSchema,
    freeShipping: z.boolean().optional(),
    maxShippingAmount: currencyOptionsSchema.optional(),
    productIdToAdd: z.string().optional(),
    variantIdToAdd: z.string().optional(),
    priceIdToAdd: z.string().optional(),
    quantityToAdd: z.number().optional(),
    productNameToAdd: z.string().optional(),
    targetProductId: z.string().optional(),
    targetProductName: z.string().optional(),
    targetVariantIds: z.array(z.string()).optional(),
    maxQuantityDiscounted: z.number().optional(),
    appliesOnEachItem: z.boolean().optional(),
    subscriptionFreeTrialDuration: z.number().optional(),
    subscriptionFreeTrialDurationType: z.enum(['day', 'week', 'month', 'year']).optional(),
    buyQuantityThreshold: z.number().optional(),
}).superRefine((action, ctx) => {
    // Per-type required fields, mirroring the server discriminated union in
    // src/app/useCases/promotions/schemas/promotionAction.schema.ts (#TAG-1512).
    // Note: for discount values we intentionally require a positive amount/percentage (a 0% or
    // 0-amount discount is a no-op), which is slightly stricter than the server's bare z.number().
    const requireField = (isValid: boolean, field: string, messageKey: string) => {
        if (!isValid) ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: messageKey })
    }
    switch (action.type) {
        case 'LineItemAdjustment':
        case 'OrderAdjustment':
            if (action.adjustmentType === 'amount') {
                requireField(
                    hasPositiveAmount(action.adjustmentAmount),
                    'adjustmentAmount',
                    'promotion.validation.discountAmountRequired',
                )
            } else {
                requireField(
                    typeof action.adjustmentPercentage === 'number' && action.adjustmentPercentage > 0,
                    'adjustmentPercentage',
                    'promotion.validation.discountPercentageRequired',
                )
            }
            break
        case 'CreateLineItem':
            requireField(!!action.priceIdToAdd, 'priceIdToAdd', 'promotion.validation.productToAddRequired')
            break
        case 'SubscriptionLineItemFreeTrial':
            requireField(
                !!action.subscriptionFreeTrialDuration && action.subscriptionFreeTrialDuration >= 1,
                'subscriptionFreeTrialDuration',
                'promotion.validation.trialDurationRequired',
            )
            break
        case 'BOGO':
            requireField(!!action.targetProductId, 'targetProductId', 'promotion.validation.bogoBuyProductRequired')
            requireField(
                !!action.productIdToAdd && !!action.variantIdToAdd,
                'productIdToAdd',
                'promotion.validation.bogoRewardProductRequired',
            )
            break
        // ShippingAdjustment has no extra required fields.
    }
})

// Main form schema
const promotionFormSchema = z.object({
    name: z.string().min(1, 'Promotion name is required'),
    enabled: z.boolean(),
    automatic: z.boolean(),
    usageLimit: z.number().optional(),
    startDate: z.date().nullable().optional(),
    endDate: z.date().nullable().optional(),
    ruleOperator: z.enum(['AND', 'OR']),
    combinesWithOrderLevelDiscounts: z.boolean(),
    combinesWithLineItemDiscounts: z.boolean(),
    combinesWithShippingDiscounts: z.boolean(),
    forceCombine: z.boolean(),
    oncePerCustomer: z.boolean(),
    rules: z.array(ruleSchema),
    actions: z.array(actionSchema).min(1, 'promotion.validation.atLeastOneAction'),
})

export type PromotionFormData = z.infer<typeof promotionFormSchema>
export type Rule = z.infer<typeof ruleSchema>
export type Action = z.infer<typeof actionSchema>

// DOM ids of every invalid field, in document order, so the invalid-submit handler can scroll to
// the first one that has a rendered element. Top-level fields use their own name (e.g. `name`);
// rule/action fields use `rules.<i>.<field>` / `actions.<i>.<field>`, matching the ids the rule
// and action cards put on their inline error messages.
const collectErrorFieldIds = (errors: FieldErrors<PromotionFormData>): string[] => {
    const ids: string[] = []
    for (const [key, value] of Object.entries(errors)) {
        if (!value) continue
        // A leaf field error (e.g. `name`) OR an array-level error (e.g. `actions` .min(1)) carries a
        // string `message`, so the scroll target is the bare field/section name. A per-row map
        // (`rules`/`actions` rows) has no top-level `message` → expand to `<key>.<index>.<field>` ids.
        if (typeof (value as { message?: unknown }).message === 'string') {
            ids.push(key)
            continue
        }
        for (const [index, rowErrors] of Object.entries(value as Record<string, FieldErrors | undefined>)) {
            if (!rowErrors) continue
            for (const field of Object.keys(rowErrors)) {
                ids.push(`${key}.${index}.${field}`)
            }
        }
    }
    return ids
}

interface PromotionFormProps {
    mode: 'create' | 'update'
    storeId: string
    initialData?: PromotionFormData
    promotion?: PromotionDetails
    isPending: boolean
    onSubmit: (data: PromotionFormData, helpers: { reset: (values?: PromotionFormData) => void }) => Promise<void>
    onCancel: () => void
    Header?: ({ isPending, onCancel, onSubmit }: { isPending: boolean, onCancel: () => void, onSubmit: () => void }) => React.ReactNode
    Aside?: () => React.ReactNode
    BottomBar?: ({ isPending, isDirty, shake, onReset, onSubmit }: { isPending: boolean, isDirty: boolean, shake: boolean, onReset: () => void, onSubmit: () => void }) => React.ReactNode
}

const getDefaultFormData = (): PromotionFormData => ({
    name: '',
    enabled: true,
    automatic: false,
    usageLimit: undefined,
    startDate: null,
    endDate: null,
    ruleOperator: 'AND',
    combinesWithOrderLevelDiscounts: false,
    combinesWithLineItemDiscounts: false,
    combinesWithShippingDiscounts: false,
    forceCombine: false,
    oncePerCustomer: false,
    rules: [],
    actions: [],
})

export const PromotionForm = ({
    mode,
    storeId,
    initialData,
    promotion,
    isPending,
    onSubmit,
    onCancel,
    Header,
    Aside,
    BottomBar,
}: PromotionFormProps) => {
    const { t } = useTranslation('stores')
    const { toast } = useToast()

    const form = useForm<PromotionFormData>({
        resolver: zodResolver(promotionFormSchema),
        defaultValues: initialData || getDefaultFormData(),
    })
    const { handleSubmit, setValue, watch, formState, reset } = form
    const isDirty = formState.isDirty

    const [shake, setShake] = React.useState(false)
    const shakeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
    const triggerShake = React.useCallback(() => {
        if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current)
        setShake(false)
        requestAnimationFrame(() => {
            setShake(true)
            shakeTimeoutRef.current = setTimeout(() => setShake(false), 700)
        })
    }, [])
    React.useEffect(() => () => {
        if (shakeTimeoutRef.current) clearTimeout(shakeTimeoutRef.current)
    }, [])

    useUnsavedChangesBlocker({ when: isDirty, onBlock: triggerShake })
    const rules = watch('rules')
    const actions = watch('actions')
    const enabled = watch('enabled')

    const handleInvalidSubmit = React.useCallback(
        (errors: FieldErrors<PromotionFormData>) => {
            // react-hook-form short-circuits on a failed Zod resolver, so without this
            // handler clicking Save would look dead. Surface the failure instead.
            triggerShake()
            toast({
                title: t('promotion.validation.title'),
                description: t('promotion.validation.description'),
                variant: 'destructive',
            })
            // Controlled fields aren't register()-ed, so RHF can't auto-focus them. Defer to the
            // next frame: RHF invokes this handler before the errors re-render commits, so the
            // inline rule/action error nodes aren't in the DOM yet. After the frame, scroll the
            // first invalid field into view — top-level (e.g. `name`) or a nested error message
            // (`rules.<i>.<field>` / `actions.<i>.<field>`).
            requestAnimationFrame(() => {
                const field = collectErrorFieldIds(errors)
                    .map((id) => document.getElementById(id))
                    .find((el): el is HTMLElement => el !== null)
                field?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                field?.focus({ preventScroll: true })
            })
        },
        [triggerShake, toast, t],
    )

    const onFormSubmit = handleSubmit(async (data) => {
        await onSubmit(data, { reset: (values) => reset(values ?? data) })
    }, handleInvalidSubmit)

    const addRule = (type: RuleType) => {
        const newRule: Rule = { type }
        if (type === 'ContainsProduct') {
            newRule.minimumQuantity = 1
        }
        if (type === 'OrderMinQuantity') {
            newRule.orderMinQuantity = 1
        }
        if (type === 'OrderMaxQuantity') {
            newRule.orderMaxQuantity = 1
        }
        if (type === 'MaxProductQuantity') {
            newRule.maximumQuantity = 1
        }
        setValue('rules', [...rules, newRule], { shouldDirty: true })
    }

    // Once the user has attempted a submit, re-run validation on each rule edit/removal so the
    // inline per-field errors clear as soon as the field becomes valid (controlled fields don't
    // trigger RHF's onChange revalidation on their own). Stays silent before the first submit.
    const revalidateAfterSubmit = { shouldValidate: formState.isSubmitted }

    const removeRule = (index: number) => {
        setValue('rules', rules.filter((_, i) => i !== index), { shouldDirty: true, ...revalidateAfterSubmit })
    }

    const updateRule = (index: number, updates: Partial<Rule>) => {
        const newRules = [...rules]
        newRules[index] = { ...newRules[index], ...updates }
        setValue('rules', newRules, { shouldDirty: true, ...revalidateAfterSubmit })
    }

    const addAction = (type: ActionType) => {
        const newAction: Action = { type }
        if (type === 'LineItemAdjustment' || type === 'OrderAdjustment') {
            newAction.adjustmentType = 'percentage'
            newAction.adjustmentPercentage = 10
        } else if (type === 'ShippingAdjustment') {
            newAction.freeShipping = true
        } else if (type === 'SubscriptionLineItemFreeTrial') {
            newAction.subscriptionFreeTrialDuration = 7
            newAction.subscriptionFreeTrialDurationType = 'day'
        } else if (type === 'BOGO') {
            newAction.buyQuantityThreshold = 1
            newAction.quantityToAdd = 1
        }
        // Match addRule: do NOT revalidate here. The "at least one action" banner clears via the
        // actions.length gate, and eager validation would surface premature errors on the new card.
        setValue('actions', [...actions, newAction], { shouldDirty: true })
    }

    const removeAction = (index: number) => {
        setValue('actions', actions.filter((_, i) => i !== index), { shouldDirty: true, ...revalidateAfterSubmit })
    }

    const updateAction = (index: number, updates: Partial<Action>) => {
        const newActions = [...actions]
        newActions[index] = { ...newActions[index], ...updates }
        setValue('actions', newActions, { shouldDirty: true, ...revalidateAfterSubmit })
    }


    const formContent = (
        <form onSubmit={onFormSubmit} noValidate className="w-full min-w-0 flex-1 space-y-6">
            {Header && <Header isPending={isPending} onCancel={onCancel} onSubmit={onFormSubmit} />}
            <PromotionSettingsCard />
            <PromotionUsageCard />
            <PromotionSchedulingCard />
            <PromotionCombinationCard />

            <PromotionRulesSection
                storeId={storeId}
                onAddRule={addRule}
                onUpdateRule={updateRule}
                onRemoveRule={removeRule}
            />

            <PromotionActionsSection
                storeId={storeId}
                onAddAction={addAction}
                onUpdateAction={updateAction}
                onRemoveAction={removeAction}
            />
        </form>
    )

    return (
        <FormProvider {...form}>
            {Aside ? (
                <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
                    {formContent}
                    <aside className="w-full min-w-0 lg:sticky lg:top-4 lg:w-[400px] lg:shrink-0">
                        <Aside />
                    </aside>
                </div>
            ) : (
                <form onSubmit={onFormSubmit} noValidate className="w-full space-y-6">
                    {Header && <Header isPending={isPending} onCancel={onCancel} onSubmit={onFormSubmit} />}
                    <PromotionSettingsCard />

                    <PromotionRulesSection
                        storeId={storeId}
                        onAddRule={addRule}
                        onUpdateRule={updateRule}
                        onRemoveRule={removeRule}
                    />

                    <PromotionActionsSection
                        storeId={storeId}
                        onAddAction={addAction}
                        onUpdateAction={updateAction}
                        onRemoveAction={removeAction}
                    />
                </form>
            )}
            {BottomBar && <BottomBar isPending={isPending} isDirty={isDirty} shake={shake} onReset={() => reset()} onSubmit={onFormSubmit} />}
        </FormProvider>
    )
}
